import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Fade from '@prism/fade';
import ReactDOM from 'react-dom';
import Button from '@prism/button';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var canUseDOM = !!(typeof window !== 'undefined' && window.document && window.document.createElement);

var Portal = function Portal(props) {
  var children = props.children,
      node = props.node;
  var defaultNode = useRef(); // eslint-disable-next-line arrow-body-style

  useEffect(function () {
    return function () {
      if (defaultNode.current) {
        document.body.removeChild(defaultNode.current);
      }

      defaultNode.current = null;
    };
  });

  if (!canUseDOM) {
    return null;
  }

  if (!node && !defaultNode.current) {
    var newNode = document.createElement('div', {
      ref: defaultNode
    });
    document.body.appendChild(newNode);
  }

  return /*#__PURE__*/ReactDOM.createPortal(children, node || defaultNode.current);
};

Portal.propTypes = {
  children: PropTypes.node.isRequired,
  node: PropTypes.node
};

// https://github.com/twbs/bootstrap/blob/v4.0.0-alpha.4/js/src/modal.js#L436-L443
var getScrollbarWidth = function getScrollbarWidth() {
  var scrollDiv = document.createElement('div'); // .modal-scrollbar-measure styles // https://github.com/twbs/bootstrap/blob/v4.0.0-alpha.4/scss/_modal.scss#L106-L113

  scrollDiv.style.position = 'absolute';
  scrollDiv.style.top = '-9999px';
  scrollDiv.style.width = '50px';
  scrollDiv.style.height = '50px';
  scrollDiv.style.overflow = 'scroll';
  document.body.appendChild(scrollDiv);
  var scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return scrollbarWidth;
};
var setScrollbarWidth = function setScrollbarWidth(padding) {
  document.body.style.paddingRight = padding > 0 ? "".concat(padding, "px") : null;
};
var conditionallyUpdateScrollbar = function conditionallyUpdateScrollbar() {
  var scrollbarWidth = getScrollbarWidth(); // https://github.com/twbs/bootstrap/blob/v4.0.0-alpha.6/js/src/modal.js#L433

  var fixedContent = document.querySelectorAll('.fixed-top, .fixed-bottom, .is-fixed, .sticky-top')[0];
  var bodyPadding = fixedContent ? parseInt(fixedContent.style.paddingRight || 0, 10) : 0;

  if (document.body.clientWidth < window.innerWidth) {
    setScrollbarWidth(bodyPadding + scrollbarWidth);
  }
};
var getOriginalBodyPadding = function getOriginalBodyPadding() {
  var style = window.getComputedStyle(document.body, null);
  return parseInt(style && style.getPropertyValue('padding-right') || 0, 10);
};

var _excluded$5 = ["autoFocus", "backdrop", "backdropClassName", "backdropTransition", "centered", "children", "className", "contentClassName", "external", "fade", "innerRef", "isOpen", "keyboard", "labelledBy", "modalClassName", "modalTransition", "onClosed", "onEnter", "onExit", "onOpened", "role", "size", "toggle", "wrapClassName", "zIndex"];

var Modal = function Modal(props) {
  var _classNames;

  var autoFocus = props.autoFocus,
      backdrop = props.backdrop,
      backdropClassName = props.backdropClassName,
      backdropTransition = props.backdropTransition,
      centered = props.centered,
      children = props.children,
      className = props.className,
      contentClassName = props.contentClassName,
      external = props.external,
      fade = props.fade,
      innerRef = props.innerRef,
      initialIsOpen = props.isOpen,
      keyboard = props.keyboard,
      labelledBy = props.labelledBy,
      modalClassName = props.modalClassName,
      modalTransition = props.modalTransition,
      onClosed = props.onClosed,
      onEnter = props.onEnter,
      onExit = props.onExit,
      onOpened = props.onOpened,
      role = props.role,
      size = props.size,
      toggle = props.toggle,
      wrapClassName = props.wrapClassName,
      zIndex = props.zIndex,
      attributes = _objectWithoutProperties(props, _excluded$5);

  var _useState = useState(initialIsOpen),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      container = _useState4[0],
      setContainer = _useState4[1];

  var triggeringElement = useRef(null);
  var originalBodyPadding = useRef(null);
  var didMount = useRef(false);
  var dialog = useRef(false);
  var mouseDownElement = useRef(false);

  var init = function init() {
    try {
      triggeringElement.current = document.activeElement;
    } catch (err) {
      triggeringElement.current = null;
    }

    var containerEl = null;
    containerEl = document.createElement('div');
    containerEl.setAttribute('tabindex', '-1');
    containerEl.style.position = 'relative';
    containerEl.style.zIndex = zIndex;
    containerEl.className = 'prism-sandbox';
    originalBodyPadding.current = getOriginalBodyPadding();
    conditionallyUpdateScrollbar();
    document.body.appendChild(containerEl);

    if (Modal.openCount === 0) {
      document.body.className = classNames(document.body.className, 'modal-open');
    }

    Modal.openCount += 1;
    setContainer(containerEl);
  };

  var destroy = function destroy() {
    if (container) {
      document.body.removeChild(container);
      setContainer(null);
    }

    if (triggeringElement.current) {
      if (typeof triggeringElement.current.focus === 'function') {
        triggeringElement.current.focus();
      }

      triggeringElement.current = null;
    }

    if (Modal.openCount <= 1) {
      var modalOpenClassName = 'modal-open'; // Use regex to prevent matching `modal-open` as part of a different
      // class, e.g. `my-modal-opened`

      var modalOpenClassNameRegex = new RegExp("(^| )".concat(modalOpenClassName, "( |$)"));
      document.body.className = document.body.className.replace(modalOpenClassNameRegex, ' ').trim();
    }

    Modal.openCount -= 1;
    setScrollbarWidth(originalBodyPadding.current);
  };

  var setFocus = function setFocus() {
    if (dialog.current && dialog.current.parentNode && typeof dialog.current.parentNode.focus === 'function') {
      dialog.current.parentNode.focus();
    }
  }; // componentDidMount


  useEffect(function () {
    if (onEnter) {
      onEnter();
    }

    if (initialIsOpen && autoFocus) {
      setFocus();
    }

    didMount.current = true;

    if (initialIsOpen) {
      init();
    } // componentWillUnmount


    return function () {
      if (onExit) {
        onExit();
      }

      if (isOpen) {
        destroy();
      }

      didMount.current = false;
    };
  }, []); // componentWillReceiveProp

  useEffect(function () {
    if (!isOpen && initialIsOpen) {
      init();
    }

    setIsOpen(initialIsOpen);
  }, [initialIsOpen]);
  useEffect(function () {
    if (autoFocus && isOpen) {
      setFocus();
    }
  }, [isOpen, autoFocus]); // when zIndex prop is updated

  useEffect(function () {
    if (container) {
      container.style.zIndex = zIndex;
    }
  }, [zIndex]);

  var handleOnEntered = function handleOnEntered(node, isAppearing) {
    onOpened();

    if (modalTransition.onEntered) {
      modalTransition.onEntered(node, isAppearing);
    }
  };

  var handleOnExited = function handleOnExited(node) {
    // so all methods get called before it is unmounted
    onClosed();

    if (modalTransition.onExited) {
      modalTransition.onExited(node);
    }

    destroy();

    if (didMount.current) {
      setIsOpen(false);
    }
  };

  var getFocusableChildren = function getFocusableChildren() {
    var focusableElements = ['a[href]', 'area[href]', 'input:not([disabled]):not([type=hidden])', 'select:not([disabled])', 'textarea:not([disabled])', 'button:not([disabled])', 'object', 'embed', 'audio[controls]', 'video[controls]', '[contenteditable]:not([contenteditable="false"])', '[tabindex]:not(.modal)'];
    return container.querySelectorAll(focusableElements.join(', '));
  };

  var getFocusedChild = function getFocusedChild() {
    var currentFocus;
    var focusableChildren = getFocusableChildren();

    try {
      currentFocus = document.activeElement;
    } catch (err) {
      var _focusableChildren = _slicedToArray(focusableChildren, 1);

      currentFocus = _focusableChildren[0];
    }

    return currentFocus;
  };

  var handleBackdropMouseDown = function handleBackdropMouseDown(e) {
    mouseDownElement.current = e.target;
  };

  var handleEscape = function handleEscape(e) {
    var isCurrentStateOpen = props.isOpen;

    if (isCurrentStateOpen && keyboard && e.keyCode === 27 && toggle) {
      toggle(e);
    }
  };

  var handleBackdropClick = function handleBackdropClick(e) {
    if (e.target === mouseDownElement.current) {
      e.stopPropagation();
      if (!initialIsOpen || backdrop !== true) return;

      if (e.target && !dialog.current.contains(e.target) && toggle) {
        toggle(e);
      }
    }
  };

  var handleTab = function handleTab(e) {
    if (e.which !== 9) return;
    var focusableChildren = getFocusableChildren();
    var totalFocusable = focusableChildren.length;
    var currentFocus = getFocusedChild();
    var focusedIndex = -1;

    for (var i = 0; i < totalFocusable; i += 1) {
      if (focusableChildren[i] === currentFocus) {
        focusedIndex = i;
        break;
      }
    }

    focusedIndex += e.shiftKey ? focusableChildren.length - 1 : 1;
    focusedIndex %= focusableChildren.length;
    e.preventDefault();
    focusableChildren[focusedIndex].focus();
  };

  var modalAttributes = {
    onClick: handleBackdropClick,
    onMouseDown: handleBackdropMouseDown,
    onKeyUp: handleEscape,
    onKeyDown: handleTab,
    style: {
      display: 'block'
    },
    'aria-labelledby': labelledBy,
    role: role,
    tabIndex: '-1'
  };

  var computedModalTransition = _objectSpread2(_objectSpread2(_objectSpread2({}, Fade.defaultProps), modalTransition), {}, {
    baseClass: fade ? modalTransition.baseClass : '',
    timeout: fade ? modalTransition.timeout : 0
  });

  var computedBackdropTransition = _objectSpread2(_objectSpread2(_objectSpread2({}, Fade.defaultProps), backdropTransition), {}, {
    baseClass: fade ? backdropTransition.baseClass : '',
    timeout: fade ? backdropTransition.timeout : 0
  });

  var renderModalDialog = function renderModalDialog() {
    return /*#__PURE__*/React.createElement("div", _extends({}, attributes, {
      className: classNames('modal-dialog', className, {
        'modal-dialog-centered': centered
      }),
      role: "document",
      ref: dialog
    }), /*#__PURE__*/React.createElement("div", {
      className: classNames('modal-content', contentClassName)
    }, children));
  };

  var Backdrop = backdrop && (fade ? /*#__PURE__*/React.createElement(Fade, _extends({}, computedBackdropTransition, {
    in: isOpen && !!backdrop,
    className: classNames('modal-backdrop', backdropClassName)
  })) : /*#__PURE__*/React.createElement("div", {
    className: classNames('modal-backdrop', 'show', backdropClassName)
  }));

  if (!isOpen || !container) {
    return null;
  }

  return /*#__PURE__*/React.createElement(Portal, {
    node: container
  }, /*#__PURE__*/React.createElement("div", {
    className: wrapClassName
  }, /*#__PURE__*/React.createElement(Fade, _extends({}, modalAttributes, computedModalTransition, {
    in: isOpen,
    onEntered: handleOnEntered,
    onExited: handleOnExited,
    className: classNames((_classNames = {
      modal: true
    }, _defineProperty(_classNames, modalClassName, modalClassName), _defineProperty(_classNames, "modal-".concat(size), size), _classNames)),
    innerRef: innerRef
  }), external, renderModalDialog()), Backdrop));
};

var FadePropTypes = PropTypes.shape(Fade.propTypes);
Modal.propTypes = {
  autoFocus: PropTypes.bool,
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['static'])]),
  backdropClassName: PropTypes.string,
  backdropTransition: FadePropTypes,
  centered: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  external: PropTypes.node,
  fade: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.func]),
  isOpen: PropTypes.bool,
  keyboard: PropTypes.bool,
  labelledBy: PropTypes.string,
  modalClassName: PropTypes.string,
  modalTransition: FadePropTypes,
  onClosed: PropTypes.func,
  onEnter: PropTypes.func,
  onExit: PropTypes.func,
  onOpened: PropTypes.func,
  role: PropTypes.string,
  size: PropTypes.string,
  toggle: PropTypes.func,
  wrapClassName: PropTypes.string,
  zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
Modal.defaultProps = {
  isOpen: false,
  autoFocus: true,
  centered: false,
  size: '',
  toggle: null,
  role: 'dialog',
  labelledBy: '',
  backdrop: true,
  onEnter: null,
  onExit: null,
  keyboard: true,
  zIndex: 10001,
  children: null,
  className: '',
  wrapClassName: '',
  modalClassName: '',
  backdropClassName: '',
  contentClassName: '',
  external: null,
  fade: true,
  onOpened: function onOpened() {},
  onClosed: function onClosed() {},
  modalTransition: {
    timeout: 300
  },
  // matches $modal-transition (bootstrap _variables.scss)
  backdropTransition: {
    mountOnEnter: true,
    timeout: 500 // matches $transition-fade (bootstrap _variables.scss)

  },
  innerRef: null
};
Modal.openCount = 0;
Modal.COMPONENT_CNAME = 'modal';
Modal.BACKDROP_CNAME = 'modal-backdrop';
Modal.OPEN_CNAME = 'modal-open';
Modal.DIALOG_CNAME = 'modal-dialog';
Modal.CONTENT_CNAME = 'modal-content';

var _excluded$4 = ["className", "tag"];

var ModalBody = function ModalBody(props) {
  var className = props.className,
      Tag = props.tag,
      attributes = _objectWithoutProperties(props, _excluded$4);

  var classes = classNames('modal-body', className);
  return /*#__PURE__*/React.createElement(Tag, _extends({}, attributes, {
    className: classes
  }));
};

ModalBody.propTypes = {
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  className: PropTypes.string
};
ModalBody.defaultProps = {
  tag: 'div',
  className: ''
};
ModalBody.COMPONENT_CNAME = 'modal-body';

var _excluded$3 = ["text", "size"];

var ModalCancel = function ModalCancel(_ref) {
  var text = _ref.text,
      size = _ref.size,
      props = _objectWithoutProperties(_ref, _excluded$3);

  return /*#__PURE__*/React.createElement(Button, _extends({
    variant: "textonly",
    size: size
  }, props), text);
};

ModalCancel.propTypes = {
  text: PropTypes.string,
  size: PropTypes.string
};
ModalCancel.defaultProps = {
  text: 'CANCEL',
  size: null
};

var _excluded$2 = ["className", "tag", "children", "size"];

var ModalFooter = function ModalFooter(props) {
  var className = props.className,
      Tag = props.tag,
      children = props.children;
      props.size;
      var attributes = _objectWithoutProperties(props, _excluded$2);

  var classes = classNames('modal-footer', className);
  return /*#__PURE__*/React.createElement(Tag, _extends({}, attributes, {
    className: classes
  }), children);
};

ModalFooter.propTypes = {
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  size: PropTypes.string
};
ModalFooter.defaultProps = {
  tag: 'div',
  className: '',
  children: null,
  size: null
};

var _excluded$1 = ["className", "children", "toggle", "tag", "wrapTag", "closeAriaLabel", "charCode", "close"];

var ModalHeader = function ModalHeader(props) {
  var className = props.className,
      children = props.children,
      toggle = props.toggle,
      Tag = props.tag,
      WrapTag = props.wrapTag,
      closeAriaLabel = props.closeAriaLabel;
      props.charCode;
      var close = props.close,
      attributes = _objectWithoutProperties(props, _excluded$1);

  var classes = classNames('modal-header', className);
  var closeButton;

  if (!close && toggle) {
    closeButton = /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: toggle,
      className: "modal-header--close close",
      "aria-label": closeAriaLabel
    }, /*#__PURE__*/React.createElement("span", {
      className: "icon prism-icon-cross"
    }));
  }

  return /*#__PURE__*/React.createElement(WrapTag, _extends({}, attributes, {
    className: classes
  }), /*#__PURE__*/React.createElement(Tag, {
    className: "modal-header-title"
  }, children), close || closeButton);
};

ModalHeader.propTypes = {
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  wrapTag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  toggle: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  closeAriaLabel: PropTypes.string,
  charCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  close: PropTypes.oneOfType([PropTypes.object])
};
ModalHeader.defaultProps = {
  tag: 'h3',
  wrapTag: 'div',
  toggle: null,
  className: '',
  children: null,
  closeAriaLabel: 'Close',
  charCode: 215,
  close: null
};

var _excluded = ["text", "size"];

var ModalOkay = function ModalOkay(_ref) {
  var text = _ref.text,
      size = _ref.size,
      props = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(Button, _extends({
    color: "primary",
    size: size
  }, props), text);
};

ModalOkay.propTypes = {
  text: PropTypes.string,
  size: PropTypes.string
};
ModalOkay.defaultProps = {
  text: 'Okay',
  size: null
};

Modal.Body = ModalBody;
Modal.Cancel = ModalCancel;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;
Modal.Okay = ModalOkay;

export { ModalBody, ModalCancel, ModalFooter, ModalHeader, ModalOkay, Modal as default };
//# sourceMappingURL=index.js.map
